import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { useMediaQuery } from 'usehooks-ts'
import { getBreadcrumbs } from '@apps/FooterApp'
import { BreadcrumbsHeader } from '@components/BreadcrumbsHeader/BreadcrumbsHeader'
import { useCategoriesGrid } from '@components/CategoriesGrid/CategoriesGridProvider'
import { useCategoryManager } from '@velasca/hooks/use-category-manager'
import { FrontImageToggler } from '@velasca/ui/front-image-toggler'
import { SansText } from '@velasca/ui/sans-text'
import { SerifTitle } from '@velasca/ui/serif-title'
import {
  CategoryHeaderContainer,
  ControlsPanel,
  QuoteStyleCategoryHeader,
  ReadMoreButton,
} from './CategoryHeader.styles'

export const CategoryHeaderSkeleton = () => {
  return (
    <CategoryHeaderContainer className="category-header">
      <Skeleton width="100%" height="13rem" />
    </CategoryHeaderContainer>
  )
}

export const CategoryHeader = ({
  showCurrentCategory,
  showOnlyBreadcrumbs = false,
  title,
  description,
  as = 'h1',
  navigation,
}: {
  showCurrentCategory?: boolean
  showOnlyBreadcrumbs?: boolean
  title?: string
  description?: string
  breadcrumbs?: React.ReactNode
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  navigation?: any
}) => {
  const isDesktop = useMediaQuery('(min-width: 992px)')
  const { t: productTranslation } = useTranslation('product')
  const descriptionRef = useRef<HTMLParagraphElement>(null)
  const [showReadMore, setShowReadMore] = useState(false)
  const [isReadMoreActive, setIsReadMoreActive] = useState(false)

  const breadcrumbs = navigation && getBreadcrumbs(navigation)

  const { currentCategory } = useCategoryManager()

  const categoryTitle = showCurrentCategory ? currentCategory?.title : title
  const categoryDescription = showCurrentCategory
    ? currentCategory?.descriptionHTML
    : description

  const { setFrontImage, frontImage } = useCategoriesGrid()

  useEffect(() => {
    if (categoryDescription) {
      const readMoreChecker = setInterval(() => {
        if (!descriptionRef.current) {
          return
        }
        if (
          descriptionRef.current.scrollHeight -
            descriptionRef.current.clientHeight >
          1 // Allowing a small margin to avoid false positives
        ) {
          setShowReadMore(true)
        }
        clearInterval(readMoreChecker)
      }, 50) // check when DOM is ready
    }
  }, [setShowReadMore, categoryDescription])

  return categoryTitle ? (
    <CategoryHeaderContainer className="category-header">
      <ControlsPanel>
        {breadcrumbs && <BreadcrumbsHeader breadcrumbs={breadcrumbs} />}
        {isDesktop && (
          <FrontImageToggler
            image={frontImage}
            onToggle={() => {
              setFrontImage(frontImage === 'standard' ? 'reverse' : 'standard')
            }}
          />
        )}
      </ControlsPanel>
      {!showOnlyBreadcrumbs && (
        <QuoteStyleCategoryHeader>
          <SerifTitle color="primary" size="md" as={as}>
            {categoryTitle}
          </SerifTitle>
          {categoryDescription && (
            <div>
              <SansText
                as="div"
                ref={descriptionRef}
                className="category-description"
                size="base"
                margin={'1.5rem 0 0 0'}
                dangerouslySetInnerHTML={{ __html: categoryDescription }}
              />
              {showReadMore && (
                <ReadMoreButton>
                  <SansText
                    underline
                    as={'span'}
                    color="primary"
                    onClick={() => {
                      if (descriptionRef.current) {
                        descriptionRef.current.classList.toggle(
                          'category-description--expanded',
                        )
                        setIsReadMoreActive((prev) => !prev)
                      }
                    }}
                  >
                    {isReadMoreActive
                      ? productTranslation('readLess')
                      : productTranslation('readMore')}
                  </SansText>
                </ReadMoreButton>
              )}
            </div>
          )}
        </QuoteStyleCategoryHeader>
      )}
    </CategoryHeaderContainer>
  ) : undefined
}
